import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignupShell from "@/components/signup/SignupShell";
import { StepName, StepLogo, StepColor, StepDomain, StepStripe, slugOf } from "@/components/signup/StepsA";
import { StepProducts, StepConsignment } from "@/components/signup/StepsB";

const TITLES = {
    1: ["What is your business called?", "Your shop opens at this address the second you go live."],
    2: ["Upload your logo", "It replaces ours on every page and every receipt."],
    3: ["Pick your brand color", "Used on buttons and accents only. Type stays black on white."],
    4: ["Your web address", "The free address is ready. Add your own domain if you have one."],
    5: ["Get paid", "This is how the shop gets paid, so it can't be skipped."],
    6: ["Pick your products", "Everything is checked. Tap a card to leave it out."],
    7: ["Who prints what", "Every product starts with 13thav printing it under your brand. Switch one off to print it yourself."],
};
const HOST = "13thav.com";
const err = (e, fallback) => e?.response?.data?.detail || fallback;

/** `/signup/shop` — one path, seven screens, seven taps to a live store (7.12). */
export default function SignupShop() {
    const { user, ready, refresh } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [state, setState] = useState(null);        // null = loading, false = no store yet
    const [step, setStep] = useState(null);
    const [form, setForm] = useState({ business_name: "", email: "", password: "" });
    const [logo, setLogo] = useState("");
    const [color, setColor] = useState("#1a1a1a");
    const [domain, setDomain] = useState("");
    const [catalog, setCatalog] = useState(null);
    const [off, setOff] = useState([]);
    const [consignOff, setConsignOff] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const absorb = useCallback((s) => {
        setState(s);
        setForm((f) => ({ ...f, business_name: s.store.name }));
        setLogo(s.store.logo || "");
        setColor(s.store.theme?.primary || "#1a1a1a");
        setDomain(s.store.custom_domain || "");
        setOff(s.products_off || []);
        setConsignOff(s.consign_off || []);
    }, []);

    // resume: a logged-in owner lands on the last unfinished step (or the ?step= Stripe sent back)
    useEffect(() => {
        if (!ready || step !== null) return;
        if (!user) { setState(false); setStep(1); return; }
        api.get("/signup/state").then(({ data }) => {
            if (data.live) { navigate("/account", { replace: true }); return; }
            absorb(data);
            const asked = Number(params.get("step"));
            setStep(asked >= 1 && asked <= data.step ? asked : Math.min(data.step, 7));
        }).catch(() => { setState(false); setStep(1); });
    }, [ready, user, step]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (step && (step === 6 || step === 7) && !catalog) {
            api.get("/signup/catalog").then(({ data }) => setCatalog(data)).catch(() => setError("Couldn't load the catalog."));
        }
    }, [step, catalog]);

    useEffect(() => {
        if (!step) return;
        const keep = params.get("stripe");
        setParams(keep ? { step: String(step), stripe: keep } : { step: String(step) }, { replace: true });
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

    const go = (n) => { setError(""); setStep(n); window.scrollTo(0, 0); };
    const save = async (body) => {
        const { data } = await api.put("/signup/step", body);
        setState(data);
        return data;
    };
    const run = async (fn) => {
        setBusy(true); setError("");
        try { await fn(); } catch (e) { setError(err(e, "Something went wrong.")); }
        finally { setBusy(false); }
    };

    const next = {
        1: () => run(async () => {
            const body = { business_name: form.business_name };
            if (!user) Object.assign(body, { email: form.email, password: form.password });
            const { data } = await api.post("/signup/start", body);
            if (data.token) { localStorage.setItem(TOKEN_KEY, data.token); await refresh(); }
            if (data.state.live) { navigate("/account", { replace: true }); return; }
            absorb(data.state);
            go(2);
        }),
        2: () => run(async () => { await save({ step: 2, logo }); go(3); }),
        3: () => run(async () => { await save({ step: 3, primary: color }); go(4); }),
        4: () => run(async () => { await save({ step: 4, custom_domain: domain }); go(5); }),
        5: () => run(async () => {
            const { data } = await api.post("/signup/stripe", { origin_url: window.location.origin });
            window.location.href = data.url;
        }),
        6: () => run(async () => { await save({ step: 6, products_off: off }); go(7); }),
        7: () => run(async () => {
            await api.post("/signup/go-live", { consign_off: consignOff });
            await refresh();
            navigate("/account?live=1", { replace: true });
        }),
    };

    if (step === null || (step > 1 && state === null) || ((step === 6 || step === 7) && !catalog && !error)) {
        return (
            <div className="flex min-h-[100dvh] items-center justify-center bg-white" data-testid="signup-loading">
                <Loader2 className="animate-spin text-muted-ink" />
            </div>
        );
    }

    const [title, lead] = TITLES[step];
    const stripe = state?.stripe;
    const stripeNote = params.get("stripe") === "done" || stripe?.account_id
        ? (stripe?.charges_enabled ? "Stripe connected." : "Stripe is still reviewing. Keep going; checkout opens when it's done.")
        : "";
    const subdomain = state?.store?.subdomain || slugOf(form.business_name) || "yourshop";
    const canNext = step !== 1 || (form.business_name.trim().length >= 2 && (user || (form.email && form.password.length >= 8)));

    return (
        <SignupShell step={step} title={title} lead={lead} accent={color} busy={busy} disabled={!canNext}
            back={step === 1 ? "/whitelabel" : () => go(step - 1)}
            error={error} onRetry={() => next[step]()}
            primary={step === 5 ? "Connect Stripe" : step === 7 ? "Go live" : "Next"} onPrimary={next[step]}>
            {step === 1 && <StepName form={form} setForm={setForm} loggedIn={!!user} host={HOST} />}
            {step === 2 && <StepLogo logo={logo} setLogo={setLogo} onColor={(c) => setColor(c)} />}
            {step === 3 && <StepColor color={color} setColor={setColor} shopName={form.business_name} logo={logo} />}
            {step === 4 && <StepDomain subdomain={subdomain} host={HOST} domain={domain} setDomain={setDomain} />}
            {step === 5 && (
                <>
                    <StepStripe status={stripe} />
                    {error && (
                        <button type="button" onClick={() => go(6)} className="mt-4 min-h-[44px] text-[15px] underline" data-testid="signup-stripe-skip">
                            I'll connect Stripe later from the back office
                        </button>
                    )}
                </>
            )}
            {step === 6 && catalog && <StepProducts catalog={catalog} off={off} setOff={setOff} stripeNote={stripeNote} />}
            {step === 7 && catalog && <StepConsignment catalog={catalog} off={off} consignOff={consignOff} setConsignOff={setConsignOff} />}
        </SignupShell>
    );
}
